import { DicomDataset } from './engine/dataset';
import { asUint16, asUint32 } from './pixelViews';
import { PIXEL_DATA_TAG } from './engine/tag';
import * as tags from './engine/tags';

/**
 * Reads a dose grid's stored voxel values from the typed dataset model.
 *
 * The point is what it does not do. The keyword-addressed projection hands
 * callers an ArrayBuffer, which for pixel data means copying the whole value
 * out of the file — 45 MB for a routine dose, to produce numbers that are
 * already sitting in the buffer that was read. The typed model holds the value
 * as a view, so the stored values can be laid over it directly.
 */

/** A dose whose pixel encoding the application cannot interpret. */
export class UnsupportedDosePixelsError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'UnsupportedDosePixelsError';
    }
}

/**
 * The stored values of a dose grid, as unsigned 32-bit integers.
 *
 * Returns undefined when the dataset's pixel data is not a plain native value
 * — encapsulated (compressed) dose data arrives as fragments, which have to be
 * decoded before anything can be read out of them.
 *
 * @throws {UnsupportedDosePixelsError} for an encoding the application does
 * not interpret. Reading a dose under the wrong encoding scales every voxel
 * wrongly while still producing a plausible grid, so it fails instead.
 */
export function readDoseStoredValues(
    dataset: DicomDataset
): Uint32Array | undefined {
    const pixelRepresentation = dataset.number(tags.PIXEL_REPRESENTATION);
    if (pixelRepresentation !== 0) {
        throw new UnsupportedDosePixelsError(
            'Dose pixel data must be unsigned ' +
                `(PixelRepresentation=${pixelRepresentation}).`
        );
    }

    const bytes = dataset.bytes(PIXEL_DATA_TAG);
    if (!bytes) {
        return undefined;
    }

    const bitsAllocated = dataset.number(tags.BITS_ALLOCATED);
    const bitsStored = dataset.number(tags.BITS_STORED);
    if (bitsAllocated !== 16 && bitsAllocated !== 32) {
        throw new UnsupportedDosePixelsError(
            `Unsupported BitsAllocated ${bitsAllocated} for dose pixel data.`
        );
    }

    const source = bitsAllocated === 16 ? asUint16(bytes) : asUint32(bytes);

    // Every allocated bit is a stored bit: the values need no masking, and a
    // 32-bit dose can be handed straight out as the view it already is.
    if (bitsStored === bitsAllocated) {
        return source instanceof Uint32Array
            ? source
            : Uint32Array.from(source);
    }

    const mask = -1 >>> (32 - (bitsStored ?? bitsAllocated));
    const masked = new Uint32Array(source.length);
    for (let i = 0; i < source.length; i++) {
        masked[i] = source[i] & mask;
    }
    return masked;
}
